import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'
import { Box, Flex, Icon, Loading } from '../../atoms'
import {
  PriceDistributionChart,
  PriceDistributionChartRef,
} from '../PriceDistributionChart'
import {
  PriceByPropertyTypeChart,
  PriceByPropertyTypeChartRef,
} from '../PriceByPropertyTypeChart'
import {
  YearlyMarketVolumeChart,
  YearlyMarketVolumeChartRef,
} from '../YearlyMarketVolumeChart'
import {
  PriceDynamicsChartProps,
  DEFAULT_PRICE_DYNAMICS_CHART_LABELS,
} from './PriceDynamicsChart.types'
import { formatCurrency, formatChange } from './PriceDynamicsChart.utils'

export interface PriceDynamicsChartRef {
  exportChart: () => string | null
  exportAllCharts: () => (string | null)[]
}

const DashboardContainer = styled.div`
  width: 100%;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 26px;
`

const MetricCard = styled(Box)`
  background: white;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.3s ease;
  }
`

const MetricValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 6px 0;
`

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3px;
`

const MetricChange = styled.div<{ $isPositive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $isPositive, theme }) =>
    $isPositive ? theme.colors.success : theme.colors.danger};
  margin-top: 6px;
`

const SparklineContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  opacity: 0.1;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
`

const ChartContainer = styled(Box)`
  background: white;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  min-height: 320px;
  display: flex;
  flex-direction: column;
`

export const PriceDynamicsChart = forwardRef<
  PriceDynamicsChartRef,
  PriceDynamicsChartProps
>(({ data, loading = false, error = null, labels = {} }, ref) => {
  const mergedLabels = { ...DEFAULT_PRICE_DYNAMICS_CHART_LABELS, ...labels }
  const priceDistributionRef = useRef<PriceDistributionChartRef>(null)
  const priceByTypeRef = useRef<PriceByPropertyTypeChartRef>(null)
  const yearlyVolumeRef = useRef<YearlyMarketVolumeChartRef>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      return priceDistributionRef.current?.exportChart() ?? null
    },
    exportAllCharts: () => [
      priceDistributionRef.current?.exportChart() ?? null,
      priceByTypeRef.current?.exportChart() ?? null,
      yearlyVolumeRef.current?.exportChart() ?? null,
    ],
  }))

  if (loading) {
    return (
      <DashboardContainer>
        <Flex justify="center" align="center" style={{ minHeight: '400px' }}>
          <Loading size="large" />
        </Flex>
      </DashboardContainer>
    )
  }

  if (error || !data) {
    return (
      <DashboardContainer>
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: '400px', color: '#999' }}
        >
          {error || mergedLabels.noData}
        </Flex>
      </DashboardContainer>
    )
  }

  return (
    <DashboardContainer>
      <MetricsGrid>
        <MetricCard>
          <MetricLabel>{mergedLabels.avgTransactionPrice}</MetricLabel>
          <MetricValue>{formatCurrency(data.avgTransactionPrice)}</MetricValue>
          <MetricChange $isPositive={data.avgTransactionPriceChange >= 0}>
            <Icon
              name={
                data.avgTransactionPriceChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.avgTransactionPriceChange)} {mergedLabels.yoy}
          </MetricChange>
          <SparklineContainer>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 40"
              preserveAspectRatio="none"
            >
              <polyline
                points={data.sparklineData
                  .map(
                    (v, i) =>
                      `${(i * 100) / (data.sparklineData.length - 1)},${40 - v * 0.4}`
                  )
                  .join(' ')}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </SparklineContainer>
        </MetricCard>

        <MetricCard>
          <MetricLabel>{mergedLabels.totalMarketVolume}</MetricLabel>
          <MetricValue>{formatCurrency(data.totalMarketVolume)}</MetricValue>
          <MetricChange $isPositive={data.totalMarketVolumeChange >= 0}>
            <Icon
              name={
                data.totalMarketVolumeChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.totalMarketVolumeChange)} {mergedLabels.yoy}
          </MetricChange>
        </MetricCard>

        <MetricCard>
          <MetricLabel>{mergedLabels.priceVelocityIndex}</MetricLabel>
          <MetricValue>{data.priceVelocityIndex}</MetricValue>
          <MetricChange $isPositive={data.priceVelocityIndexChange >= 0}>
            <Icon
              name={
                data.priceVelocityIndexChange >= 0
                  ? 'trending-up'
                  : 'trending-down'
              }
              size="small"
            />
            {formatChange(data.priceVelocityIndexChange)} {mergedLabels.mom}
          </MetricChange>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        <ChartContainer>
          <PriceDistributionChart
            ref={priceDistributionRef}
            data={data.priceDistribution}
            loading={false}
            error={null}
            title={mergedLabels.priceDistribution!}
            formatCurrency={formatCurrency}
          />
        </ChartContainer>

        <ChartContainer>
          <PriceByPropertyTypeChart
            ref={priceByTypeRef}
            data={data.pricePerM2ByType}
            loading={false}
            error={null}
            title={mergedLabels.priceByPropertyType!}
            formatCurrency={formatCurrency}
            orientation="horizontal"
          />
        </ChartContainer>

        {data.yearlyMarketVolume && data.yearlyMarketVolume.length > 0 && (
          <ChartContainer>
            <YearlyMarketVolumeChart
              ref={yearlyVolumeRef}
              data={data.yearlyMarketVolume}
              loading={false}
              error={null}
              title={mergedLabels.yearlyMarketVolume!}
              height={350}
            />
          </ChartContainer>
        )}
      </ChartsGrid>
    </DashboardContainer>
  )
})

PriceDynamicsChart.displayName = 'PriceDynamicsChart'
